/* eslint max-lines-per-function: ["error", {max: 165}] -- flag-gated v1/v2 group event layouts */
import {
  AnalyticsComponent,
  AnalyticsFieldName,
} from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsEvent,
  ScheduleAnalyticsSources,
} from "@/constants/analytics/Scheduling";
import { ModalPath } from "@/constants/Navigation";
import { ScheduleTestID, TextTestID } from "@/constants/TestID";
import { IllustrationSize } from "@/constants/UI";
import { router } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/services/Analytics";
import { useScheduleEventDetailsModalDispatch } from "@/store/reducers/modals/scheduleEventDetailsModal";
import CoreTag from "@/ui/components/groupClasses/series/CoreTag";
import EventAttendeesList from "@/ui/components/schedule/EventCardAttendeesList";
import EventCardTimeDisplay from "@/ui/components/schedule/EventCardTimeDisplay";
import JoinZoomButton from "@/ui/components/schedule/JoinZoomButton";
import { Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { joinTestIDs } from "@/utils/string.util";

type ScheduleEventCardProps = {
  appointmentMetaData?: AppointmentDetailResponse;
  attendees: BaseUserAvatarProps[];
  description?: string;
  endDate: string;
  externalId: string;
  frequency: EventRecurrence;
  groupClassExternalId?: string;
  // v2 group section events: the tapped occurrence's section, so the details screen can
  // scope to it instead of the first registered section
  groupSectionId?: string;
  // v2 group section events: renders a "Core" tag and moves the icon to the top-left
  hasCoreTag?: boolean;
  iconName?: string;
  isGroupClassEvent?: boolean;
  isPhoneCallEvent?: boolean;
  isRecurring?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  isSensitive?: boolean;
  patientId?: string;
  selectedUserId: string;
  startDate: string;
  title: string;
  typeGroup?: AppointmentTypeGroup;
};

// eslint-disable-next-line no-restricted-syntax -- schedule event card aggregates many optional event fields
const ScheduleEventCard = ({
  appointmentMetaData,
  attendees,
  description,
  endDate,
  externalId,
  frequency,
  groupClassExternalId,
  groupSectionId,
  hasCoreTag,
  iconName,
  isGroupClassEvent,
  isPhoneCallEvent,
  isRecurring,
  patientId,
  selectedUserId,
  startDate,
  title,
  typeGroup,
}: ScheduleEventCardProps) => {
  const { track } = useAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();

  const { setScheduleEventDetailsData } =
    useScheduleEventDetailsModalDispatch();

  // v2 redesign: group events show the illustration top-left with a Core tag top-right
  const shouldUseRedesignedGroupLayout =
    isGroupClassRedesignEnabled && Boolean(isGroupClassEvent);

  const modalTitle = isGroupClassEvent
    ? "Event details"
    : "Appointment details";

  const baseTestID = joinTestIDs(externalId, ScheduleTestID.ScheduleEventCard);

  const handleCardPress = () => {
    track(
      isGroupClassEvent
        ? ScheduleAnalyticsEvent.GroupEventCardPressed
        : ScheduleAnalyticsEvent.AppointmentCardPressed,
      {
        [AnalyticsFieldName.Component]: AnalyticsComponent.Card,
        [AnalyticsFieldName.EventId]: externalId,
        [AnalyticsFieldName.EventTitle]: title,
        [AnalyticsFieldName.Source]: ScheduleAnalyticsSources.ScheduleScreen,
      },
    );

    setScheduleEventDetailsData({
      appointmentMetaData,
      eventDetails: {
        attendees,
        description,
        endDate,
        externalId,
        frequency,
        groupClassExternalId,
        groupSectionId,
        iconName,
        isGroupClassEvent,
        isPhoneCallEvent,
        isRecurring,
        patientExternalId: selectedUserId,
        startDate,
        title,
        typeGroup: typeGroup || "THERAPY",
      },
      isUpcomingEvent: false,
      selectedUserId,
      title: modalTitle,
    });

    router.navigate(ModalPath.ScheduleEventDetails);
  };

  const illustration = iconName ? (
    <Box testID={joinTestIDs(baseTestID, "illustration")}>
      {getIllustrationIcon({
        height: IllustrationSize.Card,
        iconName,
        width: IllustrationSize.Card,
      })}
    </Box>
  ) : null;

  const coreTag = hasCoreTag ? (
    <Box>
      <CoreTag
        testID={joinTestIDs(baseTestID, ScheduleTestID.GroupEventCoreTag)}
      />
    </Box>
  ) : null;

  return (
    <BaseCard onPress={handleCardPress} testID={baseTestID}>
      <VStack gap={6}>
        {shouldUseRedesignedGroupLayout ? (
          <VStack gap={6}>
            <HStack alignItems="center" justifyContent="space-between">
              {illustration}
              {coreTag}
            </HStack>
            <Text
              testID={joinTestIDs(baseTestID, TextTestID.Title)}
              variant="bodyMedium"
            >
              {title}
            </Text>
            <EventCardTimeDisplay
              endDate={endDate}
              isRecurring={isRecurring}
              startDate={startDate}
              testID={baseTestID}
            />
          </VStack>
        ) : (
          <HStack gap={18} justifyContent="space-between">
            <VStack flexShrink={1} gap={6}>
              <Text
                testID={joinTestIDs(baseTestID, TextTestID.Title)}
                variant="bodyMedium"
              >
                {title}
              </Text>
              <EventCardTimeDisplay
                endDate={endDate}
                isRecurring={isRecurring}
                startDate={startDate}
                testID={baseTestID}
              />
            </VStack>
            {isGroupClassEvent && illustration}
          </HStack>
        )}

        <HStack
          alignItems="center"
          justifyContent="space-between"
          marginTop={6}
        >
          <EventAttendeesList attendees={attendees} />
          <JoinZoomButton
            endDate={endDate}
            eventId={externalId}
            eventTitle={title}
            isGroupClass={isGroupClassEvent}
            isPhoneCallEvent={isPhoneCallEvent}
            patientId={patientId ?? selectedUserId}
            selectedUserId={selectedUserId}
            startDate={startDate}
          />
        </HStack>
      </VStack>
    </BaseCard>
  );
};

export type { ScheduleEventCardProps };

export default ScheduleEventCard;
